/**
 * urlListParams — pure encode/decode for the URL-synced list-state params
 * (`?filters=`, `?sort=`, `?search=`) shared by the table list page and
 * PipelinePage.
 *
 * Doctrine (mirrors useActiveListView's param handling):
 * - Component state is authoritative; these params are a mirror, the
 *   mount-time seed, and the back/forward transport.
 * - Decoding FAILS CLOSED: any malformed input decodes to null ("param
 *   absent") — a mangled link must never crash the page or partially apply.
 * - `?filters=` carries the wire-shape FilterGroup[] (the same serialization
 *   saved views persist and the query endpoint consumes) inside a versioned
 *   JSON envelope `{"v":1,"g":[...]}`. URLSearchParams handles the
 *   percent-encoding — the raw value is plain JSON, never pre-encoded.
 * - An encoded EMPTY array (`{"v":1,"g":[]}`) is meaningful: "explicitly no
 *   filters" (the user cleared default chips), distinct from an absent param
 *   ("use the surface's defaults").
 * - Large filter sets (≥ FILTERS_COMPRESS_THRESHOLD chars of JSON) are encoded
 *   as raw-deflate + base64url with a `z1.` prefix, keeping URLs shareable
 *   even for complex saved-view combinations. Small sets stay as plain JSON
 *   for readability. Both forms decode transparently; legacy plain-JSON links
 *   continue to work unchanged.
 */

import { deflateSync, inflateSync, strFromU8, strToU8 } from 'fflate';
import type { SortDescriptor } from 'react-aria-components';

import type {
  AssociationFilterOperator,
  CrmFilter,
  FilterGroup,
  PropertyFilterOperator,
} from '@/types/crm';
import {
  isAssociatedPropertyFilter,
  isAssociationFilter,
  isEntityIdFilter,
  isPropertyFilter,
} from '@/types/crm';

/** URL param carrying the versioned filter-groups JSON envelope. */
export const FILTERS_PARAM = 'filters';
/** URL param carrying `<column>:<asc|desc>`. */
export const SORT_PARAM = 'sort';
/** URL param carrying the plain search text. */
export const SEARCH_PARAM = 'search';
/**
 * URL param carrying the display mode (table vs board).
 *
 * This is orthogonal to `?view=` (WHICH view is selected): a saved view can be
 * rendered as either a table or a board, so the two are combinable and must
 * never share a param. `mode` previously lived in `?view=`; see
 * LEGACY_MODE_TOKENS in useActiveListView for the migration.
 */
export const MODE_PARAM = 'mode';

/** Envelope schema version for `?filters=`. Bump + migrate on shape changes. */
const FILTERS_PARAM_VERSION = 1;

/**
 * Encoding version tag for the compressed `?filters=` form: raw-deflate + base64url.
 * Separate from FILTERS_PARAM_VERSION (the ENVELOPE version) so the algorithm can change
 * (a future z2.) without touching envelope semantics. fflate deflateSync/inflateSync
 * tree-shakes to ~4.6 KB gzipped; chosen over the async CompressionStream because
 * readInitialListUrlState seeds filter state synchronously on mount.
 */
const FILTERS_ENCODING_TAG = 'z1.';
/** Compress only when the plain-JSON envelope exceeds this many chars; below it stays readable. */
const FILTERS_COMPRESS_THRESHOLD = 600;
/** Reject a z1. value longer than this BEFORE decompressing (decompression-bomb input guard). */
const FILTERS_COMPRESSED_MAX_LEN = 4096;
/** Reject a z1. value whose INFLATED JSON exceeds this (decompression-bomb output guard). A legit 25-group set inflates to ~4.9 KB, so this is ~13x margin. */
const FILTERS_DECODED_MAX_LEN = 65536;

const PROPERTY_OPERATORS: ReadonlySet<PropertyFilterOperator> = new Set([
  'exact',
  'ne',
  'isnull',
  'gt',
  'gte',
  'lt',
  'lte',
  'startswith',
  'endswith',
  'contains',
  'not_contains',
  'in',
  'notin',
]);

const ASSOCIATION_OPERATORS: ReadonlySet<AssociationFilterOperator> = new Set([
  'exact',
  'ne',
  'in',
  'notin',
  'isnull',
  'in_or_isnull',
]);

const ENTITY_ID_OPERATORS: ReadonlySet<string> = new Set(['in', 'notin']);

/**
 * Structural validation of a decoded clause value. Values are intentionally
 * checked loosely (primitives, arrays, or structured objects like
 * QuantityValue) — the backend re-validates; this guard only rejects shapes no
 * legitimate clause can carry (functions can't appear in JSON; undefined can't
 * either, so present-but-null is the loosest legitimate form).
 */
function isValidFilterValue(value: unknown): boolean {
  if (value === null) {
    return true;
  }
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') {
    return true;
  }
  if (Array.isArray(value)) {
    return true;
  }
  // Structured values (QuantityValue etc.)
  return t === 'object';
}

/** Validates one decoded clause against the CrmFilter union. */
function isValidClause(raw: unknown): raw is CrmFilter {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return false;
  }
  const clause = raw as CrmFilter;
  if (!('operator' in clause) || typeof clause.operator !== 'string') {
    return false;
  }
  if (!('value' in clause) || !isValidFilterValue(clause.value)) {
    return false;
  }

  // Order matters: associated-property before association (both carry
  // `association`), entity-id before property (distinct `subject` key).
  if (isEntityIdFilter(clause)) {
    return (
      ENTITY_ID_OPERATORS.has(clause.operator) &&
      Array.isArray(clause.value) &&
      clause.value.every(v => typeof v === 'string')
    );
  }
  if (isAssociatedPropertyFilter(clause)) {
    return (
      typeof clause.association === 'string' &&
      typeof clause.target_property === 'string' &&
      PROPERTY_OPERATORS.has(clause.operator)
    );
  }
  if (isAssociationFilter(clause)) {
    return (
      typeof clause.association === 'string' &&
      ASSOCIATION_OPERATORS.has(clause.operator)
    );
  }
  if (isPropertyFilter(clause)) {
    return (
      typeof clause.property === 'string' &&
      PROPERTY_OPERATORS.has(clause.operator)
    );
  }
  return false;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function base64UrlToBytes(value: string): Uint8Array {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded); // throws on invalid chars — caller catches
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encode wire-shape filter groups into the `?filters=` param value.
 * Compact envelope keys (`v`, `g`) keep URLs short. Large payloads are
 * compressed with raw-deflate and encoded as base64url with a `z1.` prefix.
 */
export function encodeFiltersParam(groups: FilterGroup[]): string {
  const json = JSON.stringify({ v: FILTERS_PARAM_VERSION, g: groups });
  if (json.length < FILTERS_COMPRESS_THRESHOLD) {
    return json;
  }
  const compressed = deflateSync(strToU8(json), { level: 9 });
  return FILTERS_ENCODING_TAG + bytesToBase64Url(compressed);
}

/**
 * Shared envelope-JSON decoder. Parses and structurally validates the plain
 * JSON envelope (versioned `{"v":1,"g":[...]}` form) returning null on any
 * failure (fail closed).
 */
function decodeEnvelopeJson(raw: string): FilterGroup[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  const envelope = parsed as { v?: unknown; g?: unknown };
  if (envelope.v !== FILTERS_PARAM_VERSION || !Array.isArray(envelope.g)) {
    return null;
  }
  const groups: FilterGroup[] = [];
  for (const rawGroup of envelope.g) {
    if (
      rawGroup === null ||
      typeof rawGroup !== 'object' ||
      !Array.isArray((rawGroup as FilterGroup).filters)
    ) {
      return null;
    }
    const { filters } = rawGroup as FilterGroup;
    if (!filters.every(isValidClause)) {
      return null;
    }
    groups.push({ filters });
  }
  return groups;
}

/**
 * Decode a raw `?filters=` param value.
 *
 * Returns null for an absent or malformed param (fail closed — the caller
 * behaves as if no param were present; nothing is partially applied). Returns
 * `[]` for a valid explicitly-empty envelope. An unknown envelope version
 * decodes to null (forward-compat: future versions add migration here).
 * Accepts both the plain-JSON form and the `z1.` compressed form.
 */
export function decodeFiltersParam(raw: string | null): FilterGroup[] | null {
  if (!raw) {
    return null;
  }
  if (raw.startsWith(FILTERS_ENCODING_TAG)) {
    if (raw.length > FILTERS_COMPRESSED_MAX_LEN) {
      return null;
    }
    let json: string;
    try {
      const bytes = base64UrlToBytes(raw.slice(FILTERS_ENCODING_TAG.length));
      json = strFromU8(inflateSync(bytes));
    } catch {
      return null;
    }
    if (json.length > FILTERS_DECODED_MAX_LEN) {
      return null;
    }
    return decodeEnvelopeJson(json);
  }
  return decodeEnvelopeJson(raw);
}

/**
 * Encode a sort descriptor as `<column>:<asc|desc>`, or null when no sort is
 * active (param absent).
 */
export function encodeSortParam(
  descriptor: SortDescriptor | null
): string | null {
  if (!descriptor) {
    return null;
  }
  const direction = descriptor.direction === 'descending' ? 'desc' : 'asc';
  return `${String(descriptor.column)}:${direction}`;
}

/**
 * Decode a raw `?sort=` param value. Splits on the LAST `:` (defensive —
 * column identifiers are snake_case names today, but last-split keeps any
 * future `:`-bearing identifier working). Fails closed to null on a missing
 * column or unknown direction token.
 */
export function decodeSortParam(raw: string | null): SortDescriptor | null {
  if (!raw) {
    return null;
  }
  const splitAt = raw.lastIndexOf(':');
  if (splitAt <= 0) {
    return null;
  }
  const column = raw.slice(0, splitAt);
  const direction = raw.slice(splitAt + 1);
  if (direction !== 'asc' && direction !== 'desc') {
    return null;
  }
  return {
    column,
    direction: direction === 'desc' ? 'descending' : 'ascending',
  };
}

/**
 * Mount-time snapshot of the URL-synced list state, used to seed component
 * state synchronously (so the first query already runs with the URL's filters
 * — no unfiltered flash, no wasted request).
 */
export interface InitialListUrlState {
  /**
   * Decoded filter groups: null = param absent or invalid (seed from the
   * surface's defaults); [] = explicitly no filters (user cleared defaults).
   */
  filterGroups: FilterGroup[] | null;
  /** Decoded sort, or null when absent/invalid (seed from the default sort). */
  sortDescriptor: SortDescriptor | null;
  /** Search text, or null when absent (seed empty). */
  search: string | null;
}

/** Read all three params into a mount-time seed snapshot. */
export function readInitialListUrlState(
  params: URLSearchParams
): InitialListUrlState {
  return {
    filterGroups: decodeFiltersParam(params.get(FILTERS_PARAM)),
    sortDescriptor: decodeSortParam(params.get(SORT_PARAM)),
    search: params.get(SEARCH_PARAM),
  };
}
